import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AIChatbot from "../components/AIChatbot";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  Play,
  Clock,
  Users,
  Star,
  CheckCircle,
  Lock,
  BookOpen,
  Terminal,
  Award,
  Loader2,
  ShoppingCart,
  Tag,
} from "lucide-react";

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  // Promo
  const [promoCode, setPromoCode] = useState("");
  const [validatedPromo, setValidatedPromo] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [generalPromo, setGeneralPromo] = useState(null);

  useEffect(() => {
    fetchCourse();
    fetchGeneralPromo();
  }, [courseId]);

  const fetchGeneralPromo = async () => {
    try {
      const res = await axios.get(`${API}/promotions/active`);
      if (res.data.active && (res.data.applies_to === "all" || res.data.applies_to === "courses")) {
        setGeneralPromo(res.data);
      }
    } catch { /* no promo */ }
  };

  const validateCoursePromoCode = async () => {
    if (!promoCode.trim()) return;
    if (!user) { toast.error("Connectez-vous pour utiliser un code promo"); return; }
    setValidatingPromo(true);
    try {
      const res = await axios.post(`${API}/promotions/validate`,
        { code: promoCode, item_type: "courses" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setValidatedPromo(res.data);
      toast.success(`Code promo valide ! -${res.data.discount_percent}%`);
    } catch (error) {
      setValidatedPromo(null);
      toast.error(error.response?.data?.detail || "Code promo invalide");
    } finally { setValidatingPromo(false); }
  };

  const getBestCourseDiscount = () => {
    let best = 0;
    if (generalPromo?.active) best = generalPromo.discount_percent;
    if (validatedPromo?.discount_percent > best) best = validatedPromo.discount_percent;
    return best;
  };

  const fetchCourse = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/courses/${courseId}`, { headers });
      setCourse(response.data);
    } catch (error) {
      console.error("Error fetching course:", error);
      toast.error("Cours non trouvé");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      navigate("/auth", { state: { from: `/courses/${courseId}` } });
      return;
    }

    setPurchasing(true);
    try {
      const params = new URLSearchParams();
      params.append("course_id", courseId);
      if (validatedPromo?.code) params.append("promo_code", validatedPromo.code);
      else if (promoCode.trim()) params.append("promo_code", promoCode.trim());

      const response = await axios.post(
        `${API}/payments/checkout/course?${params.toString()}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            origin: window.location.origin,
          },
        }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'achat");
    } finally {
      setPurchasing(false);
    }
  };

  const handleStartLesson = (lessonId) => {
    if (!user) {
      navigate("/auth", { state: { from: `/lesson/${lessonId}` } });
      return;
    }
    navigate(`/lesson/${lessonId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-heading font-bold mb-4">Cours non trouvé</h1>
          <Link to="/courses">
            <Button>Retour aux cours</Button>
          </Link>
        </div>
      </div>
    );
  }

  const levelLabels = {
    beginner: "Débutant",
    intermediate: "Intermédiaire",
    advanced: "Avancé",
  };

  // Admins et instructeurs ont un accès complet sans achat/abonnement
  const isPrivileged = user && (user.role === "admin" || user.role === "instructor");
  const hasAccess = course.is_enrolled || isPrivileged;

  return (
    <div className="min-h-screen bg-background" data-testid="course-detail-page">
      <Navbar />

      {/* Hero Section */}
      <div className="relative bg-card/50 border-b border-border/40">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Course Info */}
            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge className="bg-primary/20 text-primary border-primary/30">
                  {course.category.toUpperCase()}
                </Badge>
                <Badge variant="outline">
                  {levelLabels[course.level]}
                </Badge>
              </div>

              <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-4" data-testid="course-title">
                {course.title}
              </h1>

              {course.short_description && (
                <p className="text-muted-foreground text-lg mb-6">
                  {course.short_description}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <span className="font-bold">{course.rating.toFixed(1)}</span>
                  <span className="text-muted-foreground">({course.reviews_count} avis)</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-5 h-5" />
                  <span>{course.students_count.toLocaleString()} étudiants</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-5 h-5" />
                  <span>{course.duration_hours} heures</span>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="font-bold text-primary">
                    {course.instructor_name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-medium">{course.instructor_name}</p>
                  <p className="text-sm text-muted-foreground">Instructeur</p>
                </div>
              </div>

              {/* À propos de ce cours */}
              <div className="mt-8 bg-card/60 border border-border rounded-lg p-6">
                <h2 className="text-lg font-heading font-bold mb-3 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  À propos de ce cours
                </h2>
                <p className="text-muted-foreground whitespace-pre-line mb-4">
                  {course.description}
                </p>

                {/* Points clés */}
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  {[
                    { icon: BookOpen, label: `${course.lessons?.length || 0} leçons structurées` },
                    { icon: Clock, label: `${course.duration_hours} heures de contenu` },
                    { icon: Terminal, label: "Labs interactifs pratiques" },
                    { icon: Award, label: "Certificat de complétion" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <item.icon className="w-4 h-4 text-primary shrink-0" />
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>

                {/* Tags */}
                {course.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-border/60">
                    {course.tags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="border-primary/30 text-primary"
                      >
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Purchase Card */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg overflow-hidden sticky top-24">
                <img
                  src={course.thumbnail || "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=800"}
                  alt={course.title}
                  className="w-full aspect-video object-cover"
                />
                <div className="p-6">
                  {(() => {
                    const disc = getBestCourseDiscount();
                    const origPrice = course.price;
                    const discPrice = disc > 0 ? (origPrice * (1 - disc / 100)).toFixed(2) : null;
                    return (
                      <div className="flex items-baseline gap-2 mb-4">
                        {discPrice ? (
                          <>
                            <span className="text-lg text-muted-foreground line-through">${origPrice}</span>
                            <span className="text-3xl font-heading font-bold text-primary">${discPrice}</span>
                            <Badge className="bg-primary/20 text-primary border-primary/30 text-xs ml-1">-{disc}%</Badge>
                          </>
                        ) : (
                          <>
                            <span className="text-3xl font-heading font-bold text-primary">${origPrice}</span>
                            <span className="text-muted-foreground line-through">${(origPrice * 1.5).toFixed(2)}</span>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* Promo code input (only if not enrolled) */}
                  {!hasAccess && (
                    <div className="mb-4">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                          <Input
                            placeholder="Code promo"
                            value={promoCode}
                            onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); if (validatedPromo) setValidatedPromo(null); }}
                            className="pl-9 font-mono uppercase text-sm h-9"
                            onKeyDown={(e) => { if (e.key === "Enter") validateCoursePromoCode(); }}
                          />
                        </div>
                        <Button variant="outline" size="sm" onClick={validateCoursePromoCode} disabled={validatingPromo || !promoCode.trim()}>
                          {validatingPromo ? <Loader2 className="w-3 h-3 animate-spin" /> : "OK"}
                        </Button>
                      </div>
                      {validatedPromo && (
                        <p className="text-xs text-primary mt-1 font-medium">
                          ✅ -{validatedPromo.discount_percent}% appliqué
                        </p>
                      )}
                    </div>
                  )}

                  {hasAccess ? (
                    <Button
                      className="w-full mb-4 glow-green"
                      onClick={() => handleStartLesson(course.lessons[0]?.lesson_id)}
                      data-testid="continue-course-btn"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {isPrivileged && !course.is_enrolled ? "Voir le cours" : "Continuer le cours"}
                    </Button>
                  ) : (
                    <Button 
                      className="w-full mb-4 glow-green" 
                      onClick={handlePurchase}
                      disabled={purchasing}
                      data-testid="purchase-course-btn"
                    >
                      {purchasing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ShoppingCart className="w-4 h-4 mr-2" />
                      )}
                      Acheter maintenant
                    </Button>
                  )}

                  <ul className="space-y-3 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      <span>Accès à vie</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      <span>{course.lessons?.length || 0} leçons</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      <span>Labs interactifs</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      <span>Certificat de complétion</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Course Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-heading font-bold mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-primary" />
              Contenu du cours
            </h2>

            <div className="space-y-3" data-testid="lessons-list">
              {course.lessons?.map((lesson, index) => (
                <div
                  key={lesson.lesson_id}
                  className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center font-mono text-sm">
                        {String(index + 1).padStart(2, "0")}
                      </div>
                      <div>
                        <h3 className="font-medium">{lesson.title}</h3>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {lesson.duration_minutes} min
                          </span>
                          {lesson.is_free && (
                            <Badge variant="outline" className="text-xs text-primary border-primary">
                              Gratuit
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {lesson.is_free || hasAccess ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStartLesson(lesson.lesson_id)}
                        data-testid={`lesson-btn-${lesson.lesson_id}`}
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Lock className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Features */}
            <div className="mt-12 grid sm:grid-cols-3 gap-6">
              <div className="bg-card border border-border rounded-lg p-6 text-center">
                <Terminal className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-heading font-bold mb-1">Labs Pratiques</h3>
                <p className="text-sm text-muted-foreground">
                  Environnements interactifs
                </p>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 text-center">
                <BookOpen className="w-8 h-8 text-secondary mx-auto mb-3" />
                <h3 className="font-heading font-bold mb-1">Quiz</h3>
                <p className="text-sm text-muted-foreground">
                  Testez vos connaissances
                </p>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 text-center">
                <Award className="w-8 h-8 text-accent mx-auto mb-3" />
                <h3 className="font-heading font-bold mb-1">Certificat</h3>
                <p className="text-sm text-muted-foreground">
                  À la complétion
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-lg p-6 sticky top-24">
              <h3 className="font-heading font-bold mb-4">Ce que vous apprendrez</h3>
              <ul className="space-y-3">
                {course.tags?.map((tag, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <span className="text-sm capitalize">{tag}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <AIChatbot />
    </div>
  );
};

export default CourseDetail;
